export const SKU_MODES = Object.freeze({ SourceArticle: 0, Template: 1, Manual: 2 })
export const EXPANSION_MODES = Object.freeze({ DependencyChildren: 0, Direct: 1 })
export const SKU_BASE_SOURCES = Object.freeze({ Article: 0, ModelCode: 1 })
export const SKU_TRANSFORMATIONS = Object.freeze({ Add: 0, Replace: 1, Remove: 2 })
export const SKU_POSITIONS = Object.freeze({ Start: 0, End: 1 })
export const SKU_PARTS = Object.freeze({ Literal: 0, Base: 1, CharacteristicValueToken: 2, BrandCharacteristicValueCode: 3, BrandVariantAttributeValueCode: 4 })
export const IMPORT_CURRENCY = 'EUR'
export const PREVIEW_PAGE_SIZE = 200

/** @typedef {{ partIndex: number, productAttributeId: number, productAttributeName: string|null, code: string, characteristicValueId: number, characteristicValue: string }} ArticleParsedAttributeValue */
/** @typedef {{ partIndex: number|null, productAttributeId: number|null, articlePosition: number, remainingText: string, reason: string }} ArticleParseDiagnostic */
/** @typedef {{ article: string, modelCode: string|null, reconstructedArticle: string|null, parseStatus: string, parsedAttributeValues: ArticleParsedAttributeValue[], diagnostic: ArticleParseDiagnostic|null, brandId: number|null }} ArticleParsing */

export function articleParsingErrorMessage(articleParsing, issues = [], productAttributeName = null) {
  if (!articleParsing || articleParsing.parseStatus === 'Success') return null
  const issueCode = (issues || []).find(issue => String(issue?.code || '').startsWith('ARTICLE_TEMPLATE_'))?.code
  if (issueCode === 'ARTICLE_TEMPLATE_CODE_NOT_FOUND') {
    const attribute = productAttributeName
      ? `атрибута «${productAttributeName}»`
      : 'значение атрибута'
    const remainder = articleParsing.diagnostic?.remainingText
    return `Не найдено подходящее ${attribute}.${remainder ? ` Оставшаяся часть артикула: ${remainder}` : ''}`
  }
  if (issueCode === 'ARTICLE_TEMPLATE_AMBIGUOUS_PARSE' || articleParsing.parseStatus === 'AmbiguousParse')
    return 'Артикул можно разобрать несколькими способами. Проверьте шаблон артикула и коды значений.'
  if (issueCode === 'ARTICLE_TEMPLATE_AMBIGUOUS_CODE_MAPPING' || articleParsing.parseStatus === 'AmbiguousCodeMapping')
    return 'Один код соответствует нескольким значениям атрибута. Проверьте настройки кодов бренда.'
  if (issueCode === 'ARTICLE_TEMPLATE_RECONSTRUCTION_MISMATCH' || articleParsing.parseStatus === 'ReconstructionMismatch')
    return 'Разобранный артикул не совпадает с исходным. Проверьте шаблон артикула.'
  if (issueCode === 'ARTICLE_TEMPLATE_INVALID' || articleParsing.parseStatus === 'InvalidTemplate')
    return 'Шаблон артикула настроен некорректно.'
  return 'Не удалось разобрать артикул по настроенному шаблону. Проверьте ModelCode, литералы и порядок частей.'
}

export function previewPageRequest(setup, offset = 0, issueOffset = 0, pageSize = PREVIEW_PAGE_SIZE, issuePageSize = PREVIEW_PAGE_SIZE, readinessFilter = null) {
  return { setup, offset, pageSize, issueOffset, issuePageSize, currencyCode: IMPORT_CURRENCY,
    ...(readinessFilter ? { readinessFilter } : {}) }
}

export function mergePreviewDrafts(current = [], incoming = []) {
  const known = new Set(current.map(draft => draft.candidateKey || draft.id))
  return [...current, ...incoming.filter(draft => {
    const key = draft.candidateKey || draft.id
    if (known.has(key)) return false
    known.add(key)
    return true
  })]
}

export function generationIssueKey(issue) {
  return [issue.code, issue.sourceRowNumber, issue.sourceColumnIndex, issue.field, issue.rawValue].join('|')
}

export function mergeGenerationIssues(current = [], incoming = []) {
  const known = new Set(current.map(generationIssueKey))
  return [...current, ...incoming.filter(issue => {
    const key = generationIssueKey(issue)
    if (known.has(key)) return false
    known.add(key)
    return true
  })]
}

const idOrNull = value => value === null || value === undefined || value === '' ? null : Number(value)

export function reconcilePriceVariantMappings(priceVariants = [], currentMappings = []) {
  const current = new Map((currentMappings || []).map(mapping => [Number(mapping.priceColumnIndex), mapping]))
  return (priceVariants || []).map(source => ({
    expansionMode: EXPANSION_MODES.Direct,
    childAttributeId: null,
    directCharacteristicValueId: null,
    parentCharacteristicValueId: null,
    ...(current.get(Number(source.priceColumnIndex)) || {}),
    priceColumnIndex: Number(source.priceColumnIndex),
    priceVariantName: String(source.priceVariantName || '').trim()
  }))
}

export function withPriceVariantExpansionMode(mapping, expansionMode) {
  return Number(expansionMode) === EXPANSION_MODES.DependencyChildren
    ? { ...mapping, expansionMode: EXPANSION_MODES.DependencyChildren, directCharacteristicValueId: null }
    : { ...mapping, expansionMode: EXPANSION_MODES.Direct, parentCharacteristicValueId: null }
}

export function withPriceVariantAttribute(mapping, childAttributeId) {
  return { ...mapping, childAttributeId: idOrNull(childAttributeId), directCharacteristicValueId: null, parentCharacteristicValueId: null }
}

export function validatePreviewSetup({ attributes = [], mappings = [], skuRule } = {}) {
  const errors = []
  attributes.forEach((attribute, index) => {
    if (!Number.isInteger(Number(attribute?.productAttributeId)) || Number(attribute.productAttributeId) <= 0)
      errors.push({ field: `attributes[${index}].productAttributeId`, message: 'Выберите характеристику.' })
    if (Number(attribute?.scope) === 1 && (!Number.isInteger(Number(attribute.commonCharacteristicValueId)) || Number(attribute.commonCharacteristicValueId) <= 0))
      errors.push({ field: `attributes[${index}].commonCharacteristicValueId`, message: 'Выберите общее значение характеристики.' })
    if (Number(attribute?.scope) === 0 && attribute.commonCharacteristicValueId !== null && attribute.commonCharacteristicValueId !== undefined && attribute.commonCharacteristicValueId !== '')
      errors.push({ field: `attributes[${index}].commonCharacteristicValueId`, message: 'Variant characteristic не должна иметь Common value.' })
  })
  mappings.forEach((mapping, index) => {
    if (!String(mapping?.priceVariantName || '').trim()) errors.push({ field: `mappings[${index}].priceVariantName`, message: 'VariantName не может быть пустым.' })
    const normalized = String(mapping?.priceVariantName || '').trim().toLocaleLowerCase()
    if (normalized && mappings.some((other, otherIndex) => otherIndex < index && String(other?.priceVariantName || '').trim().toLocaleLowerCase() === normalized))
      errors.push({ field: `mappings[${index}].priceVariantName`, message: 'VariantName должен быть уникальным.' })
    const child = Number(mapping?.childAttributeId)
    if (!Number.isInteger(child) || child <= 0) errors.push({ field: `mappings[${index}].childAttributeId`, message: `Выберите Variant attribute для ${mapping?.priceVariantName || 'ценового варианта'}.` })
    if (Number(mapping?.expansionMode) === EXPANSION_MODES.Direct) {
      if (!Number.isInteger(Number(mapping.directCharacteristicValueId)) || Number(mapping.directCharacteristicValueId) <= 0) errors.push({ field: `mappings[${index}].directCharacteristicValueId`, message: 'Выберите Direct characteristic value.' })
    } else if (Number(mapping?.expansionMode) === EXPANSION_MODES.DependencyChildren) {
      if (!Number.isInteger(Number(mapping.parentCharacteristicValueId)) || Number(mapping.parentCharacteristicValueId) <= 0) errors.push({ field: `mappings[${index}].parentCharacteristicValueId`, message: 'Выберите Parent characteristic value.' })
    } else errors.push({ field: `mappings[${index}].expansionMode`, message: 'Выберите режим expansion.' })
  })
  if (skuRule) {
    errors.push(...validateSkuRule(skuRule))
    ;(skuRule.templateParts || []).forEach((part, index) => {
      if (Number(part?.kind) !== SKU_PARTS.BrandVariantAttributeValueCode) return
      const attributeId = Number(part.productAttributeId)
      if (Number.isInteger(attributeId) && attributeId > 0 &&
        !attributes.some(attribute => Number(attribute.productAttributeId) === attributeId && Number(attribute.scope) === 0)) {
        errors.push({ field: `skuRule.templateParts[${index}].productAttributeId`, message: 'Динамический код бренда должен использовать характеристику со Scope = Variant.' })
      }
    })
  }
  return errors
}

export function validateSkuRule(rule) {
  const errors = []
  if (rule?.mode === SKU_MODES.Template) {
    const baseSource = idOrNull(rule.baseSource)
    if (![SKU_BASE_SOURCES.Article, SKU_BASE_SOURCES.ModelCode].includes(baseSource)) {
      errors.push({
        field: 'skuRule.baseSource',
        message: 'Выберите base source для Template SKU.'
      })
    }
    if (!Array.isArray(rule.templateParts) || !rule.templateParts.length) errors.push({ field: 'skuRule.templateParts', message: 'Добавьте хотя бы одну Template part.' })
    const tokenIds = new Set()
    const tokenPartIds = []
    ;(rule.templateParts || []).forEach((part, index) => {
      const kind = idOrNull(part?.kind)
      if (![SKU_PARTS.Literal, SKU_PARTS.Base, SKU_PARTS.CharacteristicValueToken, SKU_PARTS.BrandCharacteristicValueCode, SKU_PARTS.BrandVariantAttributeValueCode].includes(kind)) errors.push({ field: `skuRule.templateParts[${index}]`, message: 'Template part имеет неподдерживаемый тип.' })
      if (kind === SKU_PARTS.Literal && !String(part.literal || '').trim()) errors.push({ field: `skuRule.templateParts[${index}].literal`, message: 'Literal part не может быть пустой.' })
      if (kind === SKU_PARTS.CharacteristicValueToken) {
        if (!Number.isInteger(Number(part.characteristicValueId)) || Number(part.characteristicValueId) <= 0) errors.push({ field: `skuRule.templateParts[${index}].characteristicValueId`, message: 'CharacteristicValue token part requires a value.' })
        else tokenPartIds.push(Number(part.characteristicValueId))
      }
      if (kind === SKU_PARTS.BrandCharacteristicValueCode && (!Number.isInteger(Number(part.characteristicValueId)) || Number(part.characteristicValueId) <= 0)) errors.push({ field: `skuRule.templateParts[${index}].characteristicValueId`, message: 'Brand characteristic value code part requires a value.' })
      if (kind === SKU_PARTS.BrandVariantAttributeValueCode && (!Number.isInteger(Number(part.productAttributeId)) || Number(part.productAttributeId) <= 0)) errors.push({ field: `skuRule.templateParts[${index}].productAttributeId`, message: 'Выберите Variant attribute для динамического кода бренда.' })
    })
    ;(rule.valueTokens || []).forEach((token, index) => {
      const id = Number(token?.characteristicValueId)
      if (!Number.isInteger(id) || id <= 0) errors.push({ field: `skuRule.valueTokens[${index}].characteristicValueId`, message: 'Token должен ссылаться на CharacteristicValue.' })
      if (!String(token?.token || '').trim()) errors.push({ field: `skuRule.valueTokens[${index}].token`, message: 'Token text не может быть пустым.' })
      if (tokenIds.has(id)) errors.push({ field: `skuRule.valueTokens[${index}]`, message: 'Для одного CharacteristicValue допускается только один token.' })
      tokenIds.add(id)
    })
    tokenPartIds.forEach(id => {
      if (!tokenIds.has(id)) errors.push({ field: 'skuRule.templateParts', message: `Token part ${id} requires an explicit ValueToken.` })
    })
    ;(rule.baseTransformations || []).forEach((transformation, index) => {
      const operation = idOrNull(transformation?.operation)
      const position = idOrNull(transformation?.position)
      if (![SKU_TRANSFORMATIONS.Add, SKU_TRANSFORMATIONS.Replace, SKU_TRANSFORMATIONS.Remove].includes(operation)) errors.push({ field: `skuRule.baseTransformations[${index}]`, message: 'Неподдерживаемая transformation operation.' })
      if (![SKU_POSITIONS.Start, SKU_POSITIONS.End].includes(position)) errors.push({ field: `skuRule.baseTransformations[${index}].position`, message: 'Выберите position transformation.' })
      if ([SKU_TRANSFORMATIONS.Add, SKU_TRANSFORMATIONS.Remove].includes(operation) && !String(transformation?.value || '').trim()) errors.push({ field: `skuRule.baseTransformations[${index}].value`, message: 'Transformation value обязателен.' })
      if (operation === SKU_TRANSFORMATIONS.Replace && (!String(transformation?.from || '').trim() || !String(transformation?.to || '').trim())) errors.push({ field: `skuRule.baseTransformations[${index}]`, message: 'Replace требует From и To.' })
    })
  } else if (rule?.mode === SKU_MODES.SourceArticle || rule?.mode === SKU_MODES.Manual) {
    if ((rule.baseTransformations || []).length || (rule.templateParts || []).length || (rule.valueTokens || []).length || rule.baseSource != null) errors.push({ field: 'skuRule', message: 'Эта SKU mode не принимает Template configuration.' })
  }
  return errors
}

export function formatCharacteristic(characteristic, attributes = []) {
  const attribute = attributes.find(x => Number(x.id ?? x.productAttributeId) === Number(characteristic?.productAttributeId))
  const value = (attribute?.values || []).find(x => Number(x.id ?? x.characteristicValueId) === Number(characteristic?.characteristicValueId))
  return `${attribute?.name || `Attribute #${characteristic?.productAttributeId}`}: ${value?.value || value?.name || `Value #${characteristic?.characteristicValueId}`}`
}

export function validateLocalCharacteristics(drafts = [], attributes = [], setupAttributes = []) {
  const errors = new Map()
  const add = (id, issue) => { if (!errors.has(id)) errors.set(id, []); errors.get(id).push(issue) }
  const executionGroups = new Map()
  drafts.forEach(draft => {
    const seen = new Set()
    ;(draft.characteristics || []).forEach(characteristic => {
      const attributeId = Number(characteristic?.productAttributeId)
      const valueId = Number(characteristic?.characteristicValueId)
      if (!Number.isInteger(valueId) || valueId <= 0) add(draft.id, { code: 'CHARACTERISTIC_VALUE_REQUIRED', message: `Characteristic ${attributeId || ''} требует значение.` })
      if (seen.has(attributeId)) add(draft.id, { code: 'CHARACTERISTIC_ATTRIBUTE_DUPLICATE', message: `Attribute ${attributeId} указан более одного раза.` })
      seen.add(attributeId)
      const metadata = attributes.find(x => Number(x.id ?? x.productAttributeId) === attributeId)
      if (metadata?.values?.length && !metadata.values.some(x => Number(x.id ?? x.characteristicValueId) === valueId)) add(draft.id, { code: 'CHARACTERISTIC_VALUE_ATTRIBUTE_MISMATCH', message: `Value ${valueId} не принадлежит attribute ${attributeId}.` })
    })
    const variantByAttribute = new Map((draft.characteristics || []).filter(x => setupAttributes.find(a => Number(a.productAttributeId) === Number(x.productAttributeId))?.scope === 0).map(x => [Number(x.productAttributeId), Number(x.characteristicValueId)]))
    const variant = [...variantByAttribute.entries()].sort((a, b) => a[0] - b[0])
      .map(([attributeId, valueId]) => `${attributeId}:${valueId}`).join('|')
    const key = `${draft.excelProductTypeDraftId}|${variant}`
    if (variant) { if (!executionGroups.has(key)) executionGroups.set(key, []); executionGroups.get(key).push(draft.id) }
  })
  executionGroups.forEach(ids => { if (ids.length > 1) ids.forEach(id => add(id, { code: 'CHARACTERISTIC_COMBINATION_DUPLICATE', message: 'Variant characteristic combination дублируется.' })) })
  return errors
}

export function buildPreviewSetup({ jobId, revisionId, drafts, attributes, mappings, skuRule }) {
  const normalizedSkuRule = skuRule?.mode === SKU_MODES.Template
    ? { mode: SKU_MODES.Template, baseSource: idOrNull(skuRule.baseSource), baseTransformations: skuRule.baseTransformations || [], templateParts: skuRule.templateParts || [], valueTokens: skuRule.valueTokens || [], mismatchPolicy: skuRule.mismatchPolicy ?? 0 }
    : { mode: skuRule?.mode ?? SKU_MODES.SourceArticle, baseTransformations: [], templateParts: [], valueTokens: [], mismatchPolicy: skuRule?.mismatchPolicy ?? 0 }
  return {
    version: '1', contract: 'OfferCraft.ProductDraftSetup.V1', importJobId: jobId,
    columnMappingRevisionId: revisionId,
    productTypeDrafts: (drafts || []).map(draft => ({
      excelProductTypeDraftId: draft.excelProductTypeDraftId ?? draft.id,
      expectedVersion: draft.expectedVersion ?? draft.concurrencyVersion,
      existingProductTypeId: draft.existingProductTypeId ?? null
    })),
    attributes: (attributes || []).map(attribute => ({
      productAttributeId: idOrNull(attribute.productAttributeId),
      scope: Number(attribute.scope),
      commonCharacteristicValueId: attribute.scope === 1 ? idOrNull(attribute.commonCharacteristicValueId) : null
    })),
    priceVariantMappings: (mappings || []).map(mapping => ({
      priceColumnIndex: Number(mapping.priceColumnIndex), priceVariantName: String(mapping.priceVariantName || '').trim(),
      parentCharacteristicValueId: mapping.expansionMode === EXPANSION_MODES.DependencyChildren ? idOrNull(mapping.parentCharacteristicValueId) : null,
      childAttributeId: idOrNull(mapping.childAttributeId), expansionMode: Number(mapping.expansionMode),
      directCharacteristicValueId: mapping.expansionMode === EXPANSION_MODES.Direct ? idOrNull(mapping.directCharacteristicValueId) : null
    })),
    skuRule: normalizedSkuRule
  }
}

export function validateLocalDrafts(drafts) {
  const errors = new Map()
  const bySku = new Map()
  ;(drafts || []).forEach(draft => {
    const issues = []
    const sku = String(draft.values?.sku ?? '').trim()
    if (!sku) issues.push({ code: 'SKU_REQUIRED', message: 'Укажите SKU.' })
    if (String(draft.values?.sku ?? '').length > 100) issues.push({ code: 'SKU_TOO_LONG', message: 'SKU: максимум 100 символов.' })
    if (sku) {
      const key = sku.toLocaleLowerCase()
      if (!bySku.has(key)) bySku.set(key, [])
      bySku.get(key).push(draft.id)
    }
    const base = Number(draft.values?.basePrice)
    if (!Number.isFinite(base) || base <= 0) issues.push({ code: 'BASE_PRICE_INVALID', message: 'BasePrice должен быть больше нуля.' })
    const sale = draft.values?.salePrice
    if (sale != null && sale !== '' && (!Number.isFinite(Number(sale)) || (draft.values?.saleIsActive && (Number(sale) <= 0 || Number(sale) >= base)))) issues.push({ code: 'SALE_PRICE_INVALID', message: 'При активной скидке SalePrice должен быть больше нуля и меньше BasePrice.' })
    const quantity = Number(draft.values?.quantity)
    if (draft.values?.quantity == null || draft.values.quantity === '' || !Number.isInteger(quantity) || quantity <= 0) issues.push({ code: 'QUANTITY_INVALID', message: 'Quantity должен быть положительным целым числом.' })
    issues.push(...validateLocalImages(draft.localImages, draft.values?.isDefault !== false))
    errors.set(draft.id, issues)
  })
  bySku.forEach(ids => { if (ids.length > 1) ids.forEach(id => errors.get(id).push({ code: 'SKU_DUPLICATE', message: 'SKU дублируется среди preview товаров.' })) })
  return errors
}

// Only SKU diagnostics can be resolved by the current field editor. Mapping,
// dependency and missing characteristic configuration still require regeneration.
export const LOCAL_EDITABLE_ISSUE_CODES = Object.freeze(['SKU_MANUAL_REQUIRED', 'SKU_GENERATION_ERROR'])

export function effectiveDraftState(draft, localIssues = []) {
  const retained = (draft.issues || []).filter(issue => !LOCAL_EDITABLE_ISSUE_CODES.includes(issue.code))
  const issues = [...retained, ...localIssues.map(issue => ({ ...issue, isBlocking: true }))]
  return {
    issues,
    blocking: issues.filter(issue => issue.isBlocking).length,
    readiness: retained.some(issue => issue.isBlocking) ? 'Invalid' : issues.length ? 'NeedsReview' : 'Ready'
  }
}

export function previewSummary(result, localErrors = new Map(), drafts = result?.drafts || []) {
  if (result?.summary) return {
    productTypes: result.summary.productTypes ?? 0,
    products: result.summary.products ?? result.totalCandidates ?? drafts.length,
    ready: result.summary.ready ?? 0,
    warnings: result.summary.warnings ?? 0,
    errors: result.summary.errors ?? 0,
    total: result.summary.products ?? result.totalCandidates ?? drafts.length,
    blocking: result.blockingIssueCount ?? 0,
    warning: result.warningIssueCount ?? 0,
    needsReview: result.summary.warnings ?? 0,
    invalid: result.summary.errors ?? 0
  }
  const states = drafts.map(draft => effectiveDraftState(draft, localErrors.get(draft.id) || []))
  return {
    productTypes: result?.productTypes?.length ?? 0,
    products: result?.totalCandidates ?? drafts.length,
    warnings: states.filter(x => x.readiness === 'NeedsReview').length,
    errors: states.filter(x => x.readiness === 'Invalid').length,
    total: result?.totalCandidates ?? drafts.length,
    blocking: result?.blockingIssueCount ?? ((result?.generationIssues || []).filter(issue => issue.isBlocking).length + states.reduce((sum, state) => sum + state.blocking, 0)),
    warning: result?.warningIssueCount ?? 0,
    ready: states.filter(x => x.readiness === 'Ready').length,
    needsReview: states.filter(x => x.readiness === 'NeedsReview').length,
    invalid: states.filter(x => x.readiness === 'Invalid').length
  }
}

export const IMAGE_ACCEPT = '.jpg,.jpeg,.png,.webp,.gif'
const IMAGE_TYPES = Object.freeze({ jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif' })
export function validateImageFile(file) {
  const extension = /\.([^.]+)$/.exec(String(file?.name || ''))?.[1].toLowerCase()
  if (!Object.prototype.hasOwnProperty.call(IMAGE_TYPES, extension) || (file?.type && file.type.toLowerCase() !== IMAGE_TYPES[extension])) return { code: 'IMAGE_UNSUPPORTED', message: 'Разрешены только JPG/JPEG, PNG, WEBP и GIF с соответствующим MIME type.' }
  if (!Number.isFinite(file?.size) || file.size <= 0) return { code: 'IMAGE_EMPTY', message: 'Файл изображения пуст.' }
  if (file.size > 10 * 1024 * 1024) return { code: 'IMAGE_TOO_LARGE', message: 'Изображение превышает 10 MiB.' }
  return null
}

export function validateLocalImages(images = [], isDefault = true) {
  if (!images.length) return isDefault ? [{ code: 'IMAGE_REQUIRED', message: 'Добавьте хотя бы одно изображение для товара по умолчанию.' }] : []
  const issues = images.map(image => validateImageFile(image.file)).filter(Boolean)
  if (images.filter(image => image.isDefault).length !== 1) issues.push({ code: 'IMAGE_DEFAULT_REQUIRED', message: 'Выберите ровно одно главное изображение.' })
  return issues
}

export function hasDraftChanges(drafts = [], originals = []) {
  if (drafts.length !== originals.length) return true
  return drafts.some(draft => {
    const original = originals.find(item => item.id === draft.id)
    if (!original) return true
    const images = draft.localImages || []; const previous = original.localImages || []
    return JSON.stringify(draft.values) !== JSON.stringify(original.values) ||
      JSON.stringify(draft.characteristics || []) !== JSON.stringify(original.characteristics || []) ||
      images.length !== previous.length || images.some((image, index) => {
        const old = previous[index]
        return image.clientId !== old.clientId || image.file !== old.file || image.isDefault !== old.isDefault
      })
  })
}

export function createPreviewNavigationGuard(router, confirmLeave) {
  // Vue Router keeps the original normalized target in redirectedFrom throughout
  // a redirect chain. Object identity distinguishes even retries of the same URL.
  let acceptedChains = new WeakSet()
  const chainOf = to => to.redirectedFrom || to
  const clear = to => { if (to) acceptedChains.delete(chainOf(to)) }
  const removeAfterEach = router.afterEach(to => clear(to))
  const removeOnError = router.onError((_error, to) => clear(to))
  return {
    confirmNavigation(to) {
      const chain = chainOf(to)
      if (acceptedChains.has(chain)) return true
      if (!confirmLeave()) return false
      acceptedChains.add(chain)
      return true
    },
    dispose() {
      removeAfterEach()
      removeOnError()
      acceptedChains = new WeakSet()
    }
  }
}
